import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "cs_token";
const USER_KEY = "cs_user";
const ADMIN_TOKEN_KEY = "cs_admin_token";
const ADMIN_USER_KEY = "cs_admin_user";
const IMPERSONATION_KEY = "cs_impersonation";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [workspace, setWorkspace] = useState(null);
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const raw = localStorage.getItem(IMPERSONATION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data.user);
        setWorkspace(data.workspace);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    const me = await api.get("/auth/me");
    setWorkspace(me.data.workspace);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    localStorage.removeItem(IMPERSONATION_KEY);
    setUser(null);
    setWorkspace(null);
    setImpersonation(null);
  };

  const impersonateWorkspace = async (workspaceId) => {
    // Save current admin session before swapping
    const adminToken = localStorage.getItem(TOKEN_KEY);
    const adminUser = localStorage.getItem(USER_KEY);
    if (!adminToken || !adminUser) throw new Error("Missing admin session");

    const { data } = await api.post(`/admin/impersonate/${workspaceId}`);
    localStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
    localStorage.setItem(ADMIN_USER_KEY, adminUser);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(data.impersonation));

    // Refresh user + workspace from the new token
    const me = await api.get("/auth/me");
    localStorage.setItem(USER_KEY, JSON.stringify(me.data.user));
    setUser(me.data.user);
    setWorkspace(me.data.workspace);
    setImpersonation(data.impersonation);
    return data.impersonation;
  };

  const endImpersonation = async () => {
    try {
      await api.post("/admin/impersonate/end");
    } catch {
      // Even if the end call fails, restore admin session locally
    }
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    const adminUser = localStorage.getItem(ADMIN_USER_KEY);
    if (adminToken && adminUser) {
      localStorage.setItem(TOKEN_KEY, adminToken);
      localStorage.setItem(USER_KEY, adminUser);
    }
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    localStorage.removeItem(IMPERSONATION_KEY);
    setImpersonation(null);

    // Reload user from restored admin token
    try {
      const me = await api.get("/auth/me");
      localStorage.setItem(USER_KEY, JSON.stringify(me.data.user));
      setUser(me.data.user);
      setWorkspace(me.data.workspace);
    } catch {
      // If admin session expired, do a hard logout
      logout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        workspace,
        setWorkspace,
        login,
        logout,
        loading,
        impersonation,
        impersonateWorkspace,
        endImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
